import React, { useState, useEffect } from 'react';
import {
  Plus,
  MessageSquare,
  Edit,
  Trash2,
  Copy,
  Check,
  Eye,
  Send,
  Variable,
  FileText,
  Image,
  Video,
  File,
  Loader2
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { templatesAPI } from '../../services/api';
import { cn } from '../../lib/utils';

const WhatsAppTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'UTILITY',
    language: 'en',
    header_type: 'NONE',
    header_text: '',
    body: '',
    footer: '',
    buttons: []
  });
  
  const [newButton, setNewButton] = useState({ type: 'QUICK_REPLY', text: '', url: '', phone: '' });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const res = await templatesAPI.list();
        setTemplates(res.data || []);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
        // Set empty array if API fails
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      APPROVED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const extractVariables = (text) => {
    const matches = text?.match(/\{\{\d+\}\}/g) || [];
    return [...new Set(matches)];
  };

  const copyTemplateName = (name, id) => {
    navigator.clipboard.writeText(name);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const addButton = () => {
    if (newButton.text && newTemplate.buttons.length < 3) {
      setNewTemplate({
        ...newTemplate,
        buttons: [...newTemplate.buttons, { ...newButton, id: Date.now() }]
      });
      setNewButton({ type: 'QUICK_REPLY', text: '', url: '', phone: '' });
    }
  };

  const removeButton = (id) => {
    setNewTemplate({
      ...newTemplate,
      buttons: newTemplate.buttons.filter(b => b.id !== id)
    });
  };

  const handleCreateTemplate = async () => {
    try {
      const res = await templatesAPI.create({
        ...newTemplate,
        status: 'PENDING',
        variables: extractVariables(newTemplate.body + ' ' + newTemplate.header_text),
      });
      setTemplates([...templates, res.data]);
      setIsCreateOpen(false);
      setNewTemplate({
        name: '',
        category: 'UTILITY',
        language: 'en',
        header_type: 'NONE',
        header_text: '',
        body: '',
        footer: '',
        buttons: []
      });
    } catch (error) {
      console.error('Failed to create template:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const openPreview = (template) => {
    setPreviewTemplate(template);
    setIsPreviewOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">WhatsApp Templates</h1>
          <p className="text-slate-500 dark:text-slate-400">Create and manage message templates for lead nurturing</p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setIsCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <MessageSquare className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Templates</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{templates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Approved</p>
                <p className="text-2xl font-bold text-emerald-600">{templates.filter(t => t.status === 'APPROVED').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Variable className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Pending Review</p>
                <p className="text-2xl font-bold text-amber-600">{templates.filter(t => t.status === 'PENDING').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Messages Sent</p>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">1,247</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates Table */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="font-semibold">Template Name</TableHead>
                <TableHead className="font-semibold">Category</TableHead>
                <TableHead className="font-semibold">Header</TableHead>
                <TableHead className="font-semibold">Variables</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Created</TableHead>
                <TableHead className="w-[120px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1 bg-slate-100 dark:bg-slate-900 rounded text-sm font-mono">
                        {template.name}
                      </code>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyTemplateName(template.name, template.id)}>
                        {copiedId === template.id ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="dark:border-slate-600">{template.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {template.header_type === 'TEXT' && <FileText className="w-4 h-4 text-slate-400" />}
                      {template.header_type === 'IMAGE' && <Image className="w-4 h-4 text-blue-400" />}
                      {template.header_type === 'VIDEO' && <Video className="w-4 h-4 text-purple-400" />}
                      {template.header_type === 'DOCUMENT' && <File className="w-4 h-4 text-amber-400" />}
                      <span className="text-sm dark:text-slate-300">{template.header_type}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {template.variables.slice(0, 3).map((v, i) => (
                        <Badge key={i} variant="outline" className="text-xs dark:border-slate-600">
                          {`{{${i + 1}}}`}
                        </Badge>
                      ))}
                      {template.variables.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{template.variables.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(template.status)}>{template.status}</Badge>
                  </TableCell>
                  <TableCell className="dark:text-slate-300">{template.created_at}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openPreview(template)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm"><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Template Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create WhatsApp Template</DialogTitle>
            <DialogDescription>Create a new message template following Meta guidelines.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Template Name *</Label>
                <Input
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  placeholder="welcome_message"
                />
                <p className="text-xs text-slate-500">Lowercase, underscores only</p>
              </div>
              <div className="grid gap-2">
                <Label>Category *</Label>
                <Select value={newTemplate.category} onValueChange={(v) => setNewTemplate({...newTemplate, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTILITY">Utility</SelectItem>
                    <SelectItem value="MARKETING">Marketing</SelectItem>
                    <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Header Type</Label>
              <Select value={newTemplate.header_type} onValueChange={(v) => setNewTemplate({...newTemplate, header_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  <SelectItem value="TEXT">Text</SelectItem>
                  <SelectItem value="IMAGE">Image</SelectItem>
                  <SelectItem value="VIDEO">Video</SelectItem>
                  <SelectItem value="DOCUMENT">Document</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newTemplate.header_type === 'TEXT' && (
              <div className="grid gap-2">
                <Label>Header Text</Label>
                <Input
                  value={newTemplate.header_text}
                  onChange={(e) => setNewTemplate({...newTemplate, header_text: e.target.value})}
                  placeholder="Welcome to {{1}}!"
                />
              </div>
            )}

            <div className="grid gap-2">
              <Label>Body Text * (Use {`{{1}}, {{2}}`} for variables)</Label>
              <Textarea
                value={newTemplate.body}
                onChange={(e) => setNewTemplate({...newTemplate, body: e.target.value})}
                placeholder="Hello {{1}},\n\nThank you for your interest..."
                rows={5}
              />
              <p className="text-xs text-slate-500">Variables: {extractVariables(newTemplate.body).join(', ') || 'None'}</p>
            </div>

            <div className="grid gap-2">
              <Label>Footer (optional)</Label>
              <Input
                value={newTemplate.footer}
                onChange={(e) => setNewTemplate({...newTemplate, footer: e.target.value})}
                placeholder="Reply STOP to unsubscribe"
              />
            </div>

            <div className="grid gap-2">
              <Label>Buttons (max 3)</Label>
              <div className="flex gap-2">
                <Select value={newButton.type} onValueChange={(v) => setNewButton({...newButton, type: v})}>
                  <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="QUICK_REPLY">Quick Reply</SelectItem>
                    <SelectItem value="URL">URL</SelectItem>
                    <SelectItem value="PHONE_NUMBER">Phone</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={newButton.text}
                  onChange={(e) => setNewButton({...newButton, text: e.target.value})}
                  placeholder="Button text"
                  className="flex-1"
                />
                <Button variant="outline" onClick={addButton} disabled={newTemplate.buttons.length >= 3}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {newTemplate.buttons.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newTemplate.buttons.map(btn => (
                    <Badge key={btn.id} variant="outline" className="gap-1">
                      {btn.text}
                      <button onClick={() => removeButton(btn.id)} className="ml-1 text-red-500">×</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTemplate} disabled={!newTemplate.name || !newTemplate.body} className="bg-emerald-600 hover:bg-emerald-700">
              Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
              <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-3 max-w-[300px]">
                {previewTemplate.header_type === 'TEXT' && (
                  <p className="font-semibold text-sm mb-2 dark:text-slate-100">{previewTemplate.header_text}</p>
                )}
                <p className="text-sm whitespace-pre-line dark:text-slate-300">{previewTemplate.body}</p>
                {previewTemplate.footer && (
                  <p className="text-xs text-slate-400 mt-2">{previewTemplate.footer}</p>
                )}
                {previewTemplate.buttons.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2">
                    {previewTemplate.buttons.map((btn, i) => (
                      <button key={i} className="w-full py-2 text-sm text-emerald-600 font-medium border border-emerald-200 rounded">
                        {btn.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppTemplatesPage;
